/**
 * The tools a jobsite offers a browsing agent.
 *
 * Every one makes the same call the site's own UI makes, so an agent-driven
 * lead lands in the same inbox under the same bot protection, rate limits and
 * quota. Nothing here is an agent-only back door.
 *
 * Neither write flow completes unaided, and neither pretends to: a lead needs
 * a Turnstile token (./turnstile), and a booking needs the visitor's phone
 * proved by SMS — which is why booking is two tools.
 */

import { CORE_FIELD_KEYS, fieldsFromData, missingRequiredFromData, splitCoreValues } from '../forms/form-data';
import type { Data, FieldDef, FieldValue } from '../forms/form-data';
import { createBooking, fetchBookingAvailability, fetchBookingPublic, sendOtp, verifyOtp } from '../components/booking-widget/booking-api';
import { formInputSchema, formToolFields, labelsForIds, valuesFromInput } from './form-schema';
import { toolError, toolResult } from './model-context';
import type { BookingService } from '../components/booking-widget/booking-api';
import type { FormToolField } from './form-schema';
import type { JobsiteRuntimeConfig } from '../types/runtime-config';
import type { WebMcpTool } from './model-context';
import { getWindowJobsiteConfig } from '../types/runtime-config';
import { isPreviewMode } from "../lib/interaction-tracker";
import { requestTurnstileToken } from "./turnstile";
import { submitLead } from "../forms/submit-lead";

/** The documents behind the forms this site renders, resolved before
 *  registration. Null → the surface falls back to its preset, so the tool does. */
interface JobsiteForms {
  contact?: Data | null;
  booking?: Data | null;
}

type CallToolResult = ReturnType<typeof toolResult>;
type CreateBookingBody = Parameters<typeof createBooking>[1];

/** A config that can reach the platform API. */
type ApiConfig = JobsiteRuntimeConfig & { apiUrl: string };

/** A config that can reach this site's own endpoints: leads, reviews. */
type LeadConfig = ApiConfig & { siteId: string };

/** A step that either carries on with `value` or stops with the agent's answer. */
type Outcome<Value> = { ok: true; value: Value } | { error: CallToolResult; ok: false };

interface LoadedServices {
  ok: true;
  apiUrl: string;
  slug: string;
  services: BookingService[];
}

type BookingServices = LoadedServices | { ok: false; reason: string };

interface ChosenService {
  booking: LoadedServices;
  service: BookingService;
}

/** What book_appointment has checked before it spends anything. */
interface BookingRequest {
  code: string;
  config: ApiConfig;
  input: Record<string, unknown>;
  phone: string;
  slug: string;
}

/** A booking request ready to verify: its service, extra fields and time. */
interface TimedBooking extends BookingRequest {
  customFields: Record<string, FieldValue>;
  instant: boolean;
  service: BookingService;
  startsAt: string | undefined;
}

/** Where send_booking_code texts. */
interface CodeTarget {
  config: ApiConfig;
  phone: string;
  slug: string;
}

/** A quote request that passed its checks, as the lead endpoint takes it. */
interface ReadyQuote {
  config: LeadConfig;
  submitted: Record<string, unknown>;
}

type VerifyResult = Awaited<ReturnType<typeof verifyOtp>>;
type BookingPublicResult = Awaited<ReturnType<typeof fetchBookingPublic>>;
type LeadResult = Awaited<ReturnType<typeof submitLead>>;
type CreatedBooking = Extract<Awaited<ReturnType<typeof createBooking>>, { ok: true }>["booking"];

// Arrival windows the booking API accepts in request mode. Mirrors the widget.
const TIME_WINDOWS = ["asap", "morning", "afternoon", "evening"] as const,

  /** Enough times to choose from without a wall of them to reason through. */
  MAX_SLOTS_PER_DAY = 12,

  /** How far ahead check_availability looks: by default, and at most. */
  DEFAULT_LOOKAHEAD_DAYS = 14,
  MAX_LOOKAHEAD_DAYS = 30,

  /** How many reviews list_reviews returns: by default, and at most. */
  DEFAULT_REVIEW_LIMIT = 5,
  MAX_REVIEW_LIMIT = 20,
  MAX_STAR_RATING = 5,

  hasText = (value: string | null | undefined): value is string => Boolean(value),

  /**
   * Whether the visitor actually gave this field: not blank, zero or false.
   * @param {FieldValue} value A collected form value.
   * @returns {boolean} True when the value is filled in.
   */
  isFilled = (value: FieldValue): boolean =>
    value !== undefined && value !== "" && value !== 0 && value !== false,

  nonEmpty = (value: string | null | undefined): string | undefined => {
    if (hasText(value)) {
      return value;
    }
    return undefined;
  },

  str = (input: Record<string, unknown>, key: string): string | undefined => {
    const value = input[key];
    if (typeof value !== "string") {
      return undefined;
    }
    return nonEmpty(value.trim());
  },

  /**
   * The pageview tracker's session, so an agent lead attributes like any other.
   * Read, never minted: an id no pageview used is an orphan.
   * @returns {string | null} The session id, or null in preview or when storage is blocked.
   */
  sessionId = (): string | null => {
    if (isPreviewMode()) {
      return null;
    }
    try {
      return sessionStorage.getItem("sp_session_id");
    } catch {
      return null;
    }
  },

  /**
   * "Dana Whitfield" → first/last, because the booking API stores them apart.
   * @param {string | undefined} full The name as the visitor gave it.
   * @returns {{ firstName?: string; lastName?: string }} The first word, and the rest as the last name.
   */
  splitName = (full: string | undefined): { firstName?: string; lastName?: string } => {
    if (!hasText(full)) {
      return {};
    }
    const parts = full.split(/\s+/u);
    if (parts.length > 1) {
      return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
    }
    return { firstName: parts[0] };
  },

  contactPageUrl = (): string | null => {
    if (typeof window === "undefined") {
      return null;
    }
    return new URL("/contact", window.location.origin).href;
  },

  /**
   * What an agent should offer instead when a tool can't finish the job.
   * @param {JobsiteRuntimeConfig} config The site's runtime config.
   * @returns {Record<string, unknown>} The booking page, phone and contact page, each null when absent.
   */
  fallback = (config: JobsiteRuntimeConfig): Record<string, unknown> => {
    const business = config.business ?? {};
    return {
      bookingUrl: business.bookingUrl ?? null,
      callInstead: business.phone ?? null,
      contactPage: contactPageUrl(),
    };
  },

  /**
   * Solve the site's bot check, when it has one.
   * @param {JobsiteRuntimeConfig} config The site's runtime config, which names the Turnstile widget.
   * @returns {Promise<string | null | undefined>} The token. `undefined` → the site has no widget, so
   *   the server won't ask for one. `null` → a challenge went unsolved; callers report that rather
   *   than spend a request the server will reject.
   */
  botCheck = async (config: JobsiteRuntimeConfig): Promise<string | null | undefined> => {
    if (!hasText(config.turnstileSiteKey)) {
      return undefined;
    }
    return requestTurnstileToken(config.turnstileSiteKey);
  },

  hasApi = (config: JobsiteRuntimeConfig): config is ApiConfig => hasText(config.apiUrl),

  takesLeads = (config: JobsiteRuntimeConfig): config is LeadConfig =>
    hasApi(config) && hasText(config.siteId),

  BOT_CHECK_FAILED = "The site's bot check went unanswered, so nothing was submitted.",
  NO_ONLINE_BOOKING = "This site doesn't take online bookings.",

  servicesFrom = (apiUrl: string, slug: string, res: BookingPublicResult): BookingServices => {
    if (!res.ok) {
      return { ok: false, reason: NO_ONLINE_BOOKING };
    }
    return { apiUrl, ok: true, services: res.data.services, slug };
  },

  /**
   * Services from the booking API, which is the only list carrying ids and prices.
   * @param {JobsiteRuntimeConfig} config The site's runtime config.
   * @returns {Promise<BookingServices>} The services with the API and slug that serve them, or why
   *   the site can't book.
   */
  loadBookingServices = async (config: JobsiteRuntimeConfig): Promise<BookingServices> => {
    const { apiUrl } = config,
      slug = config.business?.slug;
    if (!hasText(apiUrl) || !hasText(slug)) {
      return { ok: false, reason: NO_ONLINE_BOOKING };
    }
    return servicesFrom(apiUrl, slug, await fetchBookingPublic(apiUrl, slug));
  },

  /**
   * Match on id, slug, or name — an agent has whatever `list_services` gave it.
   * @param {BookingService[]} services The bookable services.
   * @param {string | undefined} query What the agent called the service.
   * @returns {BookingService | undefined} The service meant, or the only one when none is named.
   */
  findService = (services: BookingService[], query: string | undefined): BookingService | undefined => {
    if (!hasText(query)) {
      if (services.length === 1) {
        return services[0];
      }
      return undefined;
    }
    const needle = query.trim().toLowerCase();
    return (
      services.find((service) => service.id === query || service.slug === needle) ??
      services.find((service) => service.name.toLowerCase() === needle) ??
      services.find((service) => service.name.toLowerCase().includes(needle))
    );
  },

  serviceFrom = (booking: LoadedServices, input: Record<string, unknown>, unnamed: string): Outcome<ChosenService> => {
    const service = findService(booking.services, str(input, "service"));
    if (!service) {
      return {
        error: toolError(unnamed, {
          services: booking.services.map((option) => ({ id: option.id, name: option.name })),
        }),
        ok: false,
      };
    }
    return { ok: true, value: { booking, service } };
  },

  /**
   * The bookable service the agent named.
   * @param {JobsiteRuntimeConfig} config The site's runtime config.
   * @param {Record<string, unknown>} input The tool's arguments, whose `service` names it.
   * @param {string} unnamed The answer when it named none this business has.
   * @returns {Promise<Outcome<ChosenService>>} The service and the API it books through, or the answer to give.
   */
  pickService = async (
    config: JobsiteRuntimeConfig,
    input: Record<string, unknown>,
    unnamed: string,
  ): Promise<Outcome<ChosenService>> => {
    const booking = await loadBookingServices(config);
    if (!booking.ok) {
      return { error: toolError(booking.reason, fallback(config)), ok: false };
    }
    return serviceFrom(booking, input, unnamed);
  },

  howToBook = (phone: string | undefined): string => {
    if (hasText(phone)) {
      return `Call ${phone}, or use request_quote to have the business get in touch.`;
    }
    return "Use request_quote to have the business get in touch.";
  },

  businessInfoTool = (): WebMcpTool => ({
    annotations: { readOnlyHint: true },
    description:
      "Facts about this business: what it does, where it works, how to reach it, its Google rating, " +
      "and whether it takes emergency work or online bookings. Call this before answering anything " +
      "about the business rather than reading it off the page.",
    execute: async () => {
      const config = getWindowJobsiteConfig(),
        {business} = config;
      if (!business || !hasText(business.name)) {
        return toolError("This page hasn't finished loading its business details.");
      }

      return toolResult({
        address: business.formattedAddress ?? null,
        bookingEnabled: business.bookingEnabled ?? false,
        bookingUrl: business.bookingUrl ?? null,
        city: business.city ?? null,
        emergencyPhone: business.emergencyPhone ?? null,
        // Null means the owner has never said either way — don't turn that into "no".
        emergencyService: business.emergencyService ?? null,
        googleRating: business.googleRating ?? null,
        googleReviewCount: business.googleReviewCount ?? null,
        name: business.name,
        open24Hours: business.is24Hours ?? false,
        phone: business.phone ?? null,
        serviceAreas: (business.serviceAreas ?? [])
          .map((area) => area.displayName ?? area.name)
          .filter(Boolean),
        services: (business.services ?? [])
          .map((service) => {
            if (typeof service === "string") {
              return service;
            }
            return service.name;
          })
          .filter(Boolean),
        state: business.state ?? null,
        website: business.website ?? business.url ?? null,
      });
    },
    inputSchema: { properties: {}, type: "object" },
    name: "get_business_info",
  }),

  openTimes = async (
    config: JobsiteRuntimeConfig,
    chosen: ChosenService,
    input: Record<string, unknown>,
  ): Promise<CallToolResult> => {
    const { booking, service } = chosen,
      days = Math.min(Math.max(Number(input.days) || DEFAULT_LOOKAHEAD_DAYS, 1), MAX_LOOKAHEAD_DAYS),
      availability = await fetchBookingAvailability(booking.apiUrl, booking.slug, service.id, days);
    if (!availability.ok) {
      return toolError(availability.error, fallback(config));
    }

    return toolResult({
      days: availability.days
        .filter((day) => day.isWorkingDay)
        .map((day) => ({
          date: day.date,
          // ISO instants — pass one back verbatim as `startsAt`.
          openTimes: day.slots
            .filter((slot) => slot.available)
            .slice(0, MAX_SLOTS_PER_DAY)
            .map((slot) => slot.startsAt),
        }))
        .filter((day) => day.openTimes.length > 0),
      durationMinutes: service.duration,
      scheduling: "instant",
      service: { id: service.id, name: service.name },
      timeZone: availability.timeZone,
    });
  },

  checkAvailabilityTool = (): WebMcpTool => ({
    annotations: { readOnlyHint: true },
    description:
      "When this business can take a job. For a service scheduled instantly this returns exact open " +
      "start times to pass to book_appointment; for one scheduled by request it returns the arrival " +
      "windows the business confirms afterwards.",
    execute: async (input) => {
      const config = getWindowJobsiteConfig(),
        picked = await pickService(config, input, "Name which service you mean.");
      if (!picked.ok) {
        return picked.error;
      }

      if (picked.value.service.schedulingMode === "request") {
        return toolResult({
          note:
            "This service is booked by request: send a preferred date and window with " +
            "book_appointment and the business confirms the exact time.",
          scheduling: "request",
          service: { id: picked.value.service.id, name: picked.value.service.name },
          timeWindows: TIME_WINDOWS,
        });
      }

      return openTimes(config, picked.value, input);
    },
    inputSchema: {
      properties: {
        service: { description: "Service id or name from list_services.", type: "string" },
        days: { description: "How many days ahead to look. 1-30, default 14.", type: "number" },
      },
      type: "object",
    },
    name: "check_availability",
  }),
  listServicesTool = (): WebMcpTool => ({
    annotations: { readOnlyHint: true },
    description:
      "The services this business offers. Bookable services come back with an id, price and " +
      "duration; pass the id or name to check_availability or book_appointment.",
    execute: async () => {
      const config = getWindowJobsiteConfig(),
        booking = await loadBookingServices(config);

      if (!booking.ok || !booking.services.length) {
        // Not bookable, but the business still lists what it does.
        const listed = (config.business?.services ?? [])
          .map((service) => {
            if (typeof service === "string") {
              return service;
            }
            return service.name;
          })
          .filter(Boolean);
        return toolResult({
          bookable: false,
          howToBook: howToBook(config.business?.phone),
          services: listed.map((name) => ({ name })),
        });
      }

      return toolResult({
        bookable: true,
        services: booking.services.map((service) => ({
          deposit: service.deposit,
          description: service.blurb || undefined,
          durationMinutes: service.duration,
          id: service.id,
          name: service.name,
          price: service.price,
          priceQualifier: service.priceQualifier,
          // "instant" takes an exact time; "request" sends a preferred day and
          // Window the business confirms.
          scheduling: service.schedulingMode,
          urgent: service.urgent,
        })),
      });
    },
    inputSchema: { properties: {}, type: "object" },
    name: "list_services",
  }),

  /** The built-in ContactForm preset — what a site with no saved form renders. */
  PRESET_QUOTE_SCHEMA = {
    properties: {
      name: { description: "The visitor's full name.", type: "string" },
      phone: { description: "Phone number the business should call back.", type: "string" },
      email: { description: "Email address, if they'd rather be emailed.", type: "string" },
      message: { description: "What they need done, in their own words.", type: "string" },
      service: { description: "Which service this is about.", type: "string" },
      address: { description: "Service address, when the job is at a property.", type: "string" },
      preferredDate: { description: "Preferred date, as YYYY-MM-DD.", type: "string" },
      preferredTime: { description: "Preferred time of day, in words.", type: "string" },
      urgency: {
        description: "How soon they need it.",
        enum: ["emergency", "urgent", "routine"],
        type: "string",
      },
    },
    required: ["name"],
    type: "object",
  } as const,

  PRESET_QUOTE_KEYS = Object.keys(PRESET_QUOTE_SCHEMA.properties),

  formFields = (form: Data | null | undefined): FieldDef[] => {
    if (form) {
      return fieldsFromData(form);
    }
    return [];
  },

  toolFieldsFor = (fields: FieldDef[]): FormToolField[] => {
    if (fields.length > 0) {
      return formToolFields(fields);
    }
    return [];
  },

  quoteSchema = (entries: FormToolField[]): Record<string, unknown> => {
    if (entries.length > 0) {
      return formInputSchema(entries);
    }
    return PRESET_QUOTE_SCHEMA;
  },

  customFieldsPart = (custom: Record<string, FieldValue>): { customFields?: Record<string, FieldValue> } => {
    if (Object.keys(custom).length > 0) {
      return { customFields: custom };
    }
    return {};
  },

  namespaceCustom = (values: Record<string, FieldValue>): Record<string, unknown> => {
    const { core, custom } = splitCoreValues(values);
    return { ...core, ...customFieldsPart(custom) };
  },

  tokenPart = (turnstileToken: string | undefined): { turnstileToken?: string } => {
    if (hasText(turnstileToken)) {
      return { turnstileToken };
    }
    return {};
  },

  quoteSent = (config: LeadConfig, result: LeadResult): CallToolResult => {
    const businessName = config.business?.name ?? "The business";
    if (result.status === "backed-up") {
      return toolResult({
        held: true,
        message:
          `${businessName} is having a temporary technical problem, so the request is held ` +
          `safely and reaches their inbox once it clears. Tell the visitor to call if urgent.`,
        sent: true,
        ...fallback(config),
      });
    }
    return toolResult({
      held: false,
      message: `${businessName} has the request and will reply directly.`,
      sent: true,
    });
  },

  sendQuote = async (quote: ReadyQuote, turnstileToken: string | null | undefined): Promise<CallToolResult> => {
    if (turnstileToken === null) {
      return toolError(BOT_CHECK_FAILED, fallback(quote.config));
    }

    const { config } = quote,
      result = await submitLead({
        apiUrl: config.apiUrl,
        backupApiUrl: config.backupApiUrl,
        businessName: config.business?.name,
        payload: {
          ...quote.submitted,
          formType: "contact",
          sessionId: sessionId(),
          // So an agent-sent lead is legible in the inbox, and measurable.
          source: "webmcp",
          variant: config.variant,
          ...tokenPart(turnstileToken),
        },
        siteId: config.siteId,
      });

    if (result.status === "failed") {
      return toolError(result.errorMessage ?? "The request couldn't be sent.", fallback(config));
    }

    return quoteSent(config, result);
  },

  requestQuoteTool = (contactForm: Data | null | undefined): WebMcpTool => {
    // The document the visitor's form is built from is the one an agent fills.
    const fields: FieldDef[] = formFields(contactForm),
      entries: FormToolField[] = toolFieldsFor(fields),
      describesForm = entries.length > 0,

      collectValues = (input: Record<string, unknown>): Record<string, FieldValue> => {
        if (describesForm) {
          return valuesFromInput(entries, input);
        }
        return Object.fromEntries(
          PRESET_QUOTE_KEYS
            .map((key): [string, FieldValue] => [key, str(input, key)])
            .filter(([, value]) => value !== undefined),
        );
      },

      /**
       * Why these values can't be sent yet.
       * @param {LeadConfig} config The site's runtime config.
       * @param {Record<string, FieldValue>} values The quote request's values, keyed by field id.
       * @returns {CallToolResult | null} The error to give the agent, or null when the request can go.
       */
      quoteProblem = (config: LeadConfig, values: Record<string, FieldValue>): CallToolResult | null => {
        if (describesForm) {
          const missing = missingRequiredFromData(contactForm, values);
          if (missing.length > 0) {
            return toolError(
              `This business's form needs: ${labelsForIds(fields, missing).join(", ")}.`,
              fallback(config),
            );
          }
        } else if (!isFilled(values.name)) {
          return toolError("A name is required to send a request.");
        }

        if (!isFilled(values.phone) && !isFilled(values.email)) {
          return toolError("A phone number or an email address is required — otherwise nobody can reply.");
        }
        return null;
      },

      // A saved form's extras are namespaced; the preset's are top-level keys,
      // Which is where ContactForm puts them and where the server reads them.
      submittedValues = (values: Record<string, FieldValue>): Record<string, unknown> => {
        if (describesForm) {
          return namespaceCustom(values);
        }
        return values;
      },

      prepareQuote = (config: JobsiteRuntimeConfig, input: Record<string, unknown>): Outcome<ReadyQuote> => {
        if (!takesLeads(config)) {
          return { error: toolError("This site can't take messages right now.", fallback(config)), ok: false };
        }

        const values = collectValues(input),
          problem = quoteProblem(config, values);
        if (problem !== null) {
          return { error: problem, ok: false };
        }
        return { ok: true, value: { config, submitted: submittedValues(values) } };
      };

    return {
      annotations: { readOnlyHint: false },
      description:
        "Send this business a quote request or message on the visitor's behalf. It arrives in the " +
        "owner's inbox exactly as the site's contact form would, and they reply directly. The fields " +
        "here are the ones this business actually asks for. Only call this with details the visitor " +
        "has given you, and tell them it has been sent.",
      execute: async (input) => {
        const prepared = prepareQuote(getWindowJobsiteConfig(), input);
        if (!prepared.ok) {
          return prepared.error;
        }
        return sendQuote(prepared.value, await botCheck(prepared.value.config));
      },
      inputSchema: quoteSchema(entries),
      name: "request_quote",
    };
  },

  textCode = async (target: CodeTarget, turnstileToken: string | null | undefined): Promise<CallToolResult> => {
    if (turnstileToken === null) {
      return toolError(BOT_CHECK_FAILED, fallback(target.config));
    }

    const res = await sendOtp(target.config.apiUrl, target.phone, {
      slug: target.slug,
      turnstileToken: turnstileToken ?? undefined,
    });
    if (!res.ok) {
      return toolError(res.error, { code: res.code, ...fallback(target.config) });
    }

    return toolResult({
      expiresInSec: 600,
      next: "Ask the visitor for the code, then call book_appointment with it.",
      sent: true,
    });
  },

  sendBookingCodeTool = (): WebMcpTool => ({
    annotations: { readOnlyHint: false },
    description:
      "Step one of booking: text a 6-digit verification code to the visitor's mobile. Booking " +
      "requires a verified phone, so call this first, ask the visitor to read out the code they " +
      "receive, then call book_appointment with it.",
    execute: async (input) => {
      const config = getWindowJobsiteConfig(),
        phone = str(input, "phone"),
        slug = config.business?.slug;
      if (!hasText(phone)) {
        return toolError("A mobile number is required.");
      }
      if (!hasApi(config) || !hasText(slug)) {
        return toolError(NO_ONLINE_BOOKING, fallback(config));
      }

      return textCode({ config, phone, slug }, await botCheck(config));
    },
    inputSchema: {
      properties: { phone: { description: "US mobile number to text the code to.", type: "string" } },
      required: ["phone"],
      type: "object",
    },
    name: "send_booking_code",
  }),

  /** Property names book_appointment defines itself — off limits to form fields. */
  BOOKING_FIXED_KEYS = [
    "phone", "code", "service", "startsAt", "preferredDate", "timeWindow",
    "name", "email", "notes", "address", "consent",
  ],

  /** Core keys the verified-customer path fills, so the form can't demand them. */
  VERIFIED_CUSTOMER_KEYS = ["name", "email", "phone"],

  arrivalWindow = (timeWindow: string | undefined): string => {
    if (hasText(timeWindow) && (TIME_WINDOWS as readonly string[]).includes(timeWindow)) {
      return timeWindow;
    }
    return "asap";
  },

  schedulingFor = (
    booking: TimedBooking,
  ): Pick<CreateBookingBody, "request"> | Pick<CreateBookingBody, "slot"> => {
    if (booking.instant && hasText(booking.startsAt)) {
      return { slot: { startsAt: booking.startsAt } };
    }
    return {
      request: {
        note: str(booking.input, "notes"),
        preferredDate: str(booking.input, "preferredDate"),
        window: arrivalWindow(str(booking.input, "timeWindow")),
      },
    };
  },

  droppedNotePart = (dropped: boolean): { noteNotAttached?: string } => {
    if (dropped) {
      return { noteNotAttached: "An exact-time booking stores no note — pass it on when you confirm, or call it through." };
    }
    return {};
  },

  bookedResult = (booking: TimedBooking, created: CreatedBooking): CallToolResult => {
    const droppedNote = booking.instant && Boolean(str(booking.input, "notes"));
    return toolResult({
      booked: true,
      confirmationCode: created.confCode,
      message: `Booked with ${booking.config.business?.name ?? "the business"}. Confirmation ${created.confCode}.`,
      service: created.serviceName,
      status: created.status,
      ...droppedNotePart(droppedNote),
    });
  },

  bookVerified = async (booking: TimedBooking, verified: VerifyResult): Promise<CallToolResult> => {
    if (!verified.ok) {
      return toolError(verified.error);
    }

    const { input, service } = booking,
      scheduling = schedulingFor(booking),

      // A known customer's own record beats a name from a chat transcript.
      supplied = splitName(str(input, "name")),
      firstName = verified.customer.firstName ?? supplied.firstName,
      lastName = verified.customer.lastName ?? supplied.lastName,

      result = await createBooking(booking.config.apiUrl, {
        consent: true,
        email: verified.customer.email ?? str(input, "email"),
        firstName,
        lastName,
        serviceId: service.id,
        slug: booking.slug,
        verificationId: verified.verificationId,
        ...scheduling,
        address: str(input, "address"),
        ...customFieldsPart(booking.customFields),
        sessionId: sessionId(),
      });
    if (!result.ok) {
      return toolError(result.error, fallback(booking.config));
    }

    return bookedResult(booking, result.booking);
  },

  confirmBooking = async (prepared: Outcome<TimedBooking>): Promise<CallToolResult> => {
    if (!prepared.ok) {
      return prepared.error;
    }
    const booking = prepared.value;
    return bookVerified(booking, await verifyOtp(booking.config.apiUrl, booking.phone, booking.code));
  },

  timedBooking = (
    request: BookingRequest,
    service: BookingService,
    customFields: Record<string, FieldValue>,
  ): Outcome<TimedBooking> => {
    const startsAt = str(request.input, "startsAt"),
      instant = service.schedulingMode === "instant";
    // The server stamps an instant booking accepted off the service's mode,
    // With or without a time, so a missing `startsAt` puts a timeless job on
    // The owner's calendar. Refused before the code is spent.
    if (instant && !hasText(startsAt)) {
      return {
        error: toolError(
          `${service.name} is booked at an exact time. Call check_availability and pass one of its open times as startsAt.`,
          { service: { id: service.id, name: service.name } },
        ),
        ok: false,
      };
    }
    return { ok: true, value: { ...request, customFields, instant, service, startsAt } };
  },

  bookAppointmentTool = (bookingForm: Data | null | undefined): WebMcpTool => {
    const allFields: FieldDef[] = formFields(bookingForm),
      // Booking takes name/email/phone from the verified customer; the form's own
      // Address and message ride the tool's `address` and `notes`. What's left is
      // The form's extra fields, which become schema properties of their own.
      extraFields = allFields.filter((field) => !CORE_FIELD_KEYS.has(field.id)),
      entries: FormToolField[] = formToolFields(extraFields, BOOKING_FIXED_KEYS),

      prepareBooking = (request: BookingRequest, picked: Outcome<ChosenService>): Outcome<TimedBooking> => {
        if (!picked.ok) {
          return picked;
        }

        const customFields = valuesFromInput(entries, request.input),
          // The form's own address/message fields are satisfied by the tool's
          // `address`/`notes`, so a required one of either is really enforced.
          missing = missingRequiredFromData(bookingForm, {
            ...customFields,
            address: str(request.input, "address"),
            message: str(request.input, "notes"),
          }).filter((id) => !VERIFIED_CUSTOMER_KEYS.includes(id));
        if (missing.length > 0) {
          return {
            error: toolError(`This business's booking form needs: ${labelsForIds(allFields, missing).join(", ")}.`),
            ok: false,
          };
        }

        return timedBooking(request, picked.value.service, customFields);
      };

    return {
      annotations: { readOnlyHint: false },
      description:
        "Step two of booking: confirm the appointment using the code texted by send_booking_code. " +
        "Pass `startsAt` for a service scheduled instantly, or `preferredDate` and `timeWindow` for " +
        "one scheduled by request. The visitor must agree to be contacted about the job before you " +
        "set `consent`.",
      execute: async (input) => {
        const config = getWindowJobsiteConfig(),
          phone = str(input, "phone"),
          code = str(input, "code"),
          slug = config.business?.slug;

        if (!hasText(phone) || !hasText(code)) {
          return toolError("The phone number and the texted code are both required.");
        }
        if (input.consent !== true) {
          return toolError("Ask the visitor to agree to be contacted about the job, then set consent.");
        }
        if (!hasApi(config) || !hasText(slug)) {
          return toolError(NO_ONLINE_BOOKING, fallback(config));
        }

        return confirmBooking(prepareBooking(
          { code, config, input, phone, slug },
          await pickService(config, input, "Name which service to book."),
        ));
      },
      inputSchema: formInputSchema(entries, {
          address: { description: "Where the job is, when it's at a property.", type: "string" },
          code: { description: "The 6-digit code the visitor received.", type: "string" },
          consent: {
            description: "True only once the visitor has agreed to be contacted about this job.",
            type: "boolean",
          },
          email: { description: "The visitor's email, for the confirmation.", type: "string" },
          name: { description: "The visitor's full name.", type: "string" },
          notes: {
            description:
              "Anything the business should know about the job. Only kept for services the business " +
              "schedules by request — an exact-time booking has nowhere to store it.",
            type: "string",
          },
          phone: { description: "The number send_booking_code texted.", type: "string" },
          preferredDate: { description: "Preferred date, as YYYY-MM-DD.", type: "string" },
          service: { description: "Service id or name from list_services.", type: "string" },
          startsAt: {
            description: "Exact start time from check_availability, for instantly scheduled services.",
            type: "string",
          },
          timeWindow: {
            description: "Preferred arrival window. Defaults to asap.",
            enum: [...TIME_WINDOWS],
            type: "string",
          },
        },
        ["phone", "code", "consent"],
      ),
      name: "book_appointment",
    };
  },
  containsRun = (hay: string[], run: string[]): boolean =>
      run.length <= hay.length &&
      hay.some((_word, start) => run.every((word, offset) => hay[start + offset] === word)),

  /**
   * The reviews endpoint for what the agent asked.
   * @param {JobsiteRuntimeConfig} config The site's runtime config.
   * @param {Record<string, unknown>} input The tool's arguments: `limit` and `minRating`.
   * @returns {string | null} The URL, or null when the site has no reviews endpoint.
   */
  reviewsUrl = (config: JobsiteRuntimeConfig, input: Record<string, unknown>): string | null => {
    if (!takesLeads(config)) {
      return null;
    }
    const limit = Math.min(Math.max(Number(input.limit) || DEFAULT_REVIEW_LIMIT, 1), MAX_REVIEW_LIMIT),
      minRating = Math.min(Math.max(Number(input.minRating) || 1, 1), MAX_STAR_RATING);
    return `${config.apiUrl}/api/sites/public/${config.siteId}/reviews?limit=${limit}&minRating=${minRating}`;
  },

  reviewsFrom = async (config: JobsiteRuntimeConfig, res: Response): Promise<CallToolResult> => {
    if (!res.ok) {
      return toolError("Couldn't load reviews.", fallback(config));
    }
    const json = (await res.json()) as {
      averageRating?: number;
      totalCount?: number;
      reviews?: {
        profileName?: string; rating?: number; reviewText?: string;
        timeAgo?: string; source?: string; ownerAnswer?: string | null;
      }[];
    };
    return toolResult({
      averageRating: json.averageRating ?? config.business?.googleRating ?? null,
      reviews: (json.reviews ?? []).map((review) => ({
        author: review.profileName ?? null,
        ownerReply: nonEmpty(review.ownerAnswer),
        rating: review.rating ?? null,
        source: review.source ?? null,
        text: nonEmpty(review.reviewText) ?? null,
        when: review.timeAgo ?? null,
      })),
      totalCount: json.totalCount ?? config.business?.googleReviewCount ?? null,
    });
  },

  listReviewsTool = (): WebMcpTool => ({
    annotations: { readOnlyHint: true },
    description:
      "What this business's customers actually said, with the rating and how long ago. Use this " +
      "rather than reading review text off the page — the page shows only what the owner chose to " +
      "feature.",
    execute: async (input) => {
      const config = getWindowJobsiteConfig(),
        url = reviewsUrl(config, input);
      if (url === null) {
        return toolError("This site has no reviews to show.");
      }

      try {
        return await reviewsFrom(config, await fetch(url));
      } catch {
        return toolError("Couldn't load reviews.", fallback(config));
      }
    },
    inputSchema: {
      properties: {
        limit: { description: "How many reviews to return. 1-20, default 5.", type: "number" },
        minRating: { description: "Lowest star rating to include, 1-5. Default 1 (all).", type: "number" },
      },
      type: "object",
    },
    name: "list_reviews",
  }),
  words = (value: string): string[] => value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean),

  /**
   * Whole words in either direction: "Fairview" is in "Fairview Heights", but
   * "Hampton" is not in "Northampton" — a substring test says it is.
   * @param {string} areaName A service area the business lists.
   * @param {string} query The place the agent asked about.
   * @returns {boolean} Whether either name's words appear in a row inside the other's.
   */
  areaMatches = (areaName: string, query: string): boolean => {
    const area = words(areaName),
      asked = words(query);
    if (!area.length || !asked.length) {
      return false;
    }
    return containsRun(area, asked) || containsRun(asked, area);
  },

  areaLabel = (area: { displayName?: string; name?: string }): string => {
    if (hasText(area.displayName)) {
      return area.displayName;
    }
    return area.name ?? "";
  },

  coverage = (config: JobsiteRuntimeConfig, location: string): CallToolResult => {
    const areas = config.business?.serviceAreas ?? [],
      named = areas
        .map((area) => ({ isPrimary: area.isPrimary, name: areaLabel(area) }))
        .filter((area) => area.name),
      match = named.find((area) => areaMatches(area.name, location));

    if (match) {
      return toolResult({
        areasServed: named.map((area) => area.name),
        isPrimaryArea: match.isPrimary ?? false,
        matchedArea: match.name,
        status: "covered",
      });
    }

    // "We also cover nearby" is a real answer, just not a definite one.
    if (areas.some((area) => area.surroundingAreas === true)) {
      return toolResult({
        areasServed: named.map((area) => area.name),
        callToConfirm: config.business?.phone ?? null,
        note: `${config.business?.name ?? "This business"} lists nearby areas and says it also covers surrounding ones, but ${location} isn't named. Confirm by phone.`,
        status: "possibly_covered",
      });
    }

    return toolResult({
      areasServed: named.map((area) => area.name),
      callToConfirm: config.business?.phone ?? null,
      note: `${location} isn't among the areas this business lists.`,
      status: "not_listed",
    });
  },

  checkServiceAreaTool = (): WebMcpTool => ({
    annotations: { readOnlyHint: true },
    description:
      "Whether this business works in a given town, suburb or city. Answer coverage questions with " +
      "this rather than guessing from the areas named on the page.",
    execute: async (input) => {
      const config = getWindowJobsiteConfig(),
        location = str(input, "location");
      if (!hasText(location)) {
        return toolError("Name the town or suburb to check.");
      }

      return coverage(config, location);
    },
    inputSchema: {
      properties: {
        location: { description: "Town, suburb, city or neighbourhood to check.", type: "string" },
      },
      required: ["location"],
      type: "object",
    },
    name: "check_service_area",
  }),

  /**
   * Every tool a jobsite registers, in the order an agent would work through them.
   * @param {JobsiteForms} forms The saved contact and booking forms the tools fill.
   * @returns {WebMcpTool[]} The tools to register.
   */
  buildJobsiteWebMcpTools = (forms: JobsiteForms = {}): WebMcpTool[] => [
    businessInfoTool(),
    listServicesTool(),
    listReviewsTool(),
    checkServiceAreaTool(),
    checkAvailabilityTool(),
    requestQuoteTool(forms.contact),
    sendBookingCodeTool(),
    bookAppointmentTool(forms.booking),
  ];

export type { JobsiteForms };
export { buildJobsiteWebMcpTools };
